import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import type { AnalysisConfigSnapshot } from "@/store/analysisConfigSnapshot";

/**
 * The parked manual analysis configuration only.
 *
 * The snapshot has its own slice, because it must stay OUTSIDE the slices that it
 * holds. The entry into agentic or onboarding mode resets `dataset`, `spatial`
 * and the other slices. Thus a snapshot in one of them would go with the reset.
 *
 * The slice is also the workspace lock. `parkAnalysisConfig` does not replace an
 * existing snapshot. Thus the first mode that parks holds the workspace until it
 * clears the snapshot. Without this rule, a second mode would park the temporary
 * state of the first mode over the real configuration of the user.
 */
export interface AnalysisConfigSnapshotState {
  snapshot: AnalysisConfigSnapshot | null;
}

export const analysisConfigSnapshotInitialState: AnalysisConfigSnapshotState = {
  snapshot: null
};

export const analysisConfigSnapshotSlice = createSlice({
  name: "analysisConfigSnapshot",
  initialState: analysisConfigSnapshotInitialState,
  reducers: {
    /**
     * The reducer does nothing if a snapshot is already parked. A caller that then
     * resets slices or removes map layers must read the snapshot back. Thus it can
     * see that it holds the workspace.
     */
    parkAnalysisConfig: (
      state,
      action: PayloadAction<AnalysisConfigSnapshot>
    ) => {
      if (state.snapshot) return;
      state.snapshot = action.payload;
    },
    clearAnalysisConfigSnapshot: () => analysisConfigSnapshotInitialState
  }
});

export const { parkAnalysisConfig, clearAnalysisConfigSnapshot } =
  analysisConfigSnapshotSlice.actions;

export const analysisConfigSnapshotReducer =
  analysisConfigSnapshotSlice.reducer;

export const selectParkedAnalysisConfig = (
  state: RootState
): AnalysisConfigSnapshot | null => state.analysisConfigSnapshot.snapshot;

export const selectHasParkedAnalysisConfig = (state: RootState): boolean =>
  state.analysisConfigSnapshot.snapshot !== null;
